// Port of companion/src/complete.mjs at tag js-core-freeze-2026-09-05 (HEAD cb2d250): autocomplete
// for the Basil script format. The same script with the same caret answers the same list: the same
// items in the same order, with the same labels, inserts, kinds, details and caret offsets. The
// only shape changes are the ones the editor-client lint profile forces (the frozen `for...of` and
// `continue` are forEach and `return`); none of them changes an output.
//
// Pure and deterministic. Everything this module can offer comes from two places and nowhere else:
// the writer's own script (its "==" headings) and the book's own words (companion/names.json:
// cast, moods, locations, variables), with the season's price ladder for the brackets. Nothing is
// invented, nothing is fetched, and an engine id is never a candidate: ids live in the sidecar and
// the writer never sees one.
//
//   complete(script, caretOffset, { names, ladder })
//     → { anchor, prefix, items: [{ label, insert, kind, detail, caret }] }
//
// `anchor` is where the typed prefix starts, so a caller replaces the text between `anchor` and
// the caret with `insert` and then puts the caret at `anchor + item.caret`. That offset is usually
// the end of what was written, but two items move it on purpose: a character line leaves it inside
// its own brackets, where the mood goes, and a mood or a price finishing a bracket the line had
// already opened steps over it. That is the whole contract: this module never touches a document,
// The regex class of the sidecar id spellings (complete.mjs:50), for the spell check:
// cspell:ignore CELV
// a file or a network.
import type {
  Completion,
  CompletionContextKey,
  CompletionDomain,
  CompletionIdTable,
  CompletionItem,
  CompletionKind,
  CompletionNames,
  CompletionOptions,
} from '../../dorian-shared/types/episodeScript';

const MAX_ITEMS = 8;

// The rungs of the season ladder as rules.ts (R2) defines them. A caller normally passes its own;
// this default is here so the provider can stand alone in a page or a test, and a test pins the
// two spellings together.
const DEFAULT_LADDER: readonly number[] = Object.freeze([5, 10, 25, 150]);

/** One reserved word of a ">" line, with the spelling the grammar expects after it. */
interface Directive {
  word: string;
  insert: string;
  detail: string;
}

// The words the format reserves for a ">" line, each with the spelling the grammar expects after
// it: a colon and a space where the directive takes a value, and nothing at all after the two
// that take none.
const DIRECTIVES: readonly Directive[] = Object.freeze([
  { word: 'if', insert: 'if: ', detail: 'check what the story remembers' },
  { word: 'else', insert: 'else', detail: 'what happens otherwise' },
  { word: 'end', insert: 'end', detail: 'this scene ends the episode' },
  { word: 'go to', insert: 'go to: ', detail: 'send the scene onward by name' },
  { word: 'remember', insert: 'remember: ', detail: 'the story remembers something' },
  { word: 'forget', insert: 'forget: ', detail: 'the story forgets it again' },
  { word: 'hub', insert: 'hub: ', detail: 'a scene readers come back to, with a heading and a line under it' },
]);

// A caret with nothing typed in front of it only opens a list where the list is short enough to
// read at a glance: the seven directives, the twelve moods, the ladder, and the scenes an arrow is
// already pointing at. Everywhere else the writer types a letter first, so an empty caret is never
// interrupted.
const OPEN_ON_EMPTY = new Set<CompletionContextKey>(['directive', 'mood', 'price', 'scene-arrow']);

// C4921500, E17, L5820016, V30836. The sidecar's spelling of a thing, never a writer's word, so it
// is never offered however it reached the table.
const ENGINE_ID = /^[CELV]-?\d+$/i;

const lower = (value: unknown): string => String(value ?? '').toLocaleLowerCase();

function lines(script: string): string[] {
  return String(script ?? '').split('\n').map((line) => line.replace(/\r$/, ''));
}

// How often the script itself already says this word, counted whole rather than inside another
// word, so "aff_ashe" does not count as a use of "ashe" and the bracket in "[25]" does not stop 25
// from counting.
const WORD_CHARACTER = /[\p{L}\p{N}_]/u;

function frequency(haystack: string, word: string): number {
  const needle = lower(word);
  if (!needle) return 0;
  let count = 0;
  let at = 0;
  for (;;) {
    const found = haystack.indexOf(needle, at);
    if (found < 0) return count;
    const before: string | undefined = haystack[found - 1];
    const after: string | undefined = haystack[found + needle.length];
    if (!(before && WORD_CHARACTER.test(before)) && !(after && WORD_CHARACTER.test(after))) count += 1;
    at = found + needle.length;
  }
}

/** complete.mjs:85 — a table whose `byId` is a Map, whatever else it carries. */
function hasIdTable(domain: object): domain is CompletionIdTable {
  return (domain as { byId?: unknown }).byId instanceof Map;
}

// companion/names.json hands each domain over as id → word; the page's copy of the same table adds
// the ids that have no word yet, spelled C…/E…/L…/V…. Both shapes arrive here, and so does the
// compiler's own {byId, byName}.
function words(domain: CompletionDomain | undefined): string[] {
  if (!domain) return [];
  let values: readonly unknown[];
  if (domain instanceof Map) values = [...domain.values()];
  else if (hasIdTable(domain)) values = [...domain.byId.values()];
  else if (Array.isArray(domain)) values = domain;
  else values = Object.values(domain);
  const seen = new Set<string>();
  const kept: string[] = [];
  values.forEach((value) => {
    const word = String(value ?? '').trim();
    if (!word || ENGINE_ID.test(word) || seen.has(lower(word))) return;
    seen.add(lower(word));
    kept.push(word);
  });
  return kept;
}

/** Where the caret is, said in the grammar's own terms (complete.mjs:104-137). */
interface Context {
  kind: CompletionKind;
  key: CompletionContextKey;
  prefix: string;
}

// Where the caret is, said in the grammar's own terms. `head` is the line in front of the caret
// and `tail` the rest of that line: a completion is offered only where the rest of the line is
// closable — empty, or the one bracket the context is already inside — so nothing is ever spliced
// into the middle of a word the writer is still standing in.
function contextAt(head: string, tail: string): Context | null {
  const open = /^\s*$/.test(tail);
  const mood = head.match(/^(\p{Lu}[\p{Lu}\p{M}\p{N} _.-]*?)\s+\(([^)]*)$/u);
  if (mood) return /^\)?\s*$/.test(tail) ? { kind: 'mood', key: 'mood', prefix: mood[2] } : null;

  const price = head.match(/^\s*[*-]\s*.*\[([^[\]]*)$/);
  if (price) return /^\]?\s*$/.test(tail) ? { kind: 'price', key: 'price', prefix: price[1] } : null;
  if (!open) return null;

  // "* answer → SCENE", "- card → SCENE", "> true → SCENE", "> else → SCENE".
  const arrow = head.match(/^\s*(?:[*-]|>)\s*.*(?:→|->)\s(.*)$/);
  if (arrow) return { kind: 'scene', key: 'scene-arrow', prefix: arrow[1] };

  const route = head.match(/^\s*>\s*go\s+to:\s*(.*)$/i);
  if (route) return { kind: 'scene', key: 'scene-goto', prefix: route[1] };

  const variable = head.match(/^\s*>\s*(?:remember|forget|if):\s*([^=]*)$/i);
  if (variable) return { kind: 'variable', key: 'variable', prefix: variable[1] };

  const directive = head.match(/^\s*>\s*([A-Za-z]*(?:\s[A-Za-z]*)?)$/);
  if (directive) return { kind: 'directive', key: 'directive', prefix: directive[1] };

  const location = head.match(/^==\s.*\s@\s(.*)$/);
  if (location) return { kind: 'location', key: 'location', prefix: location[1] };

  // A character line is NAME in caps at the start of a line. One capital on its own is far more
  // often the first letter of a sentence, so the cast waits until there are two.
  // Round 1, lane A (spec B.3): the caps are any script's, as the parser's NAME pattern is — a cast
  // named ÉMILE or one written in Cyrillic is offered the same way. Verified against
  // basil-golden/complete/cases.json:
  // no case moves, because every case's cast line is ASCII.
  const character = head.match(/^(\p{Lu}[\p{Lu}\p{M}\p{N} _.-]*)$/u);
  if (character && character[1].trim().length >= 2) {
    return { kind: 'character', key: 'character', prefix: character[1] };
  }
  return null;
}

/** One thing that could be offered, before ranking: an item plus the word it is matched on. */
interface Candidate {
  label: string;
  insert: string;
  kind: CompletionKind;
  detail: string;
  match: string;
  caret?: number;
}

function sceneCandidates(script: string, skipLine: number): Candidate[] {
  const seen = new Set<string>();
  const found: Candidate[] = [];
  lines(script).forEach((line, index) => {
    // Never the line the caret is standing on: a name half typed is not yet a name, and offering
    // it back would complete the writer with themselves.
    if (index === skipLine || !/^==(?:\s|$)/.test(line)) return;
    const body = line.replace(/^==\s*/, '').trim();
    const marker = body.lastIndexOf(' @ ');
    const name = (marker < 0 ? body : body.slice(0, marker)).trim();
    if (!name || seen.has(lower(name))) return;
    seen.add(lower(name));
    found.push({
      label: name, insert: name, kind: 'scene', detail: 'a scene in this script', match: name,
    });
  });
  return found;
}

function characterCandidates(names: CompletionNames): Candidate[] {
  return words(names.characters).map((word): Candidate => {
    const spelled = word.toLocaleUpperCase();
    return {
      // The format's own spelling of a character line, with the caret left between the brackets
      // so the mood is the next thing the writer picks.
      label: `${spelled} (mood)`,
      insert: `${spelled} ()`,
      caret: spelled.length + 2,
      kind: 'character',
      detail: 'a character, with the mood next',
      match: spelled,
    };
  });
}

// The bracket is closed here if the line has not closed it already; where it has, the caret steps
// over the one that is there instead of being left inside it. Either way the writer comes out the
// far side of the mood.
function moodCandidates(names: CompletionNames, tail: string): Candidate[] {
  const closed = tail.startsWith(')');
  return words(names.expressions).map((word): Candidate => ({
    label: word,
    insert: closed ? word : `${word})`,
    caret: word.length + 1,
    kind: 'mood',
    detail: 'a mood the book has',
    match: word,
  }));
}

function locationCandidates(names: CompletionNames): Candidate[] {
  return words(names.locations).map((word): Candidate => ({
    label: word, insert: word, kind: 'location', detail: 'a location the book has', match: word,
  }));
}

// The book's variables, and any the writer has already used in this script: a name invented on
// line 4 should be offered again on line 400 rather than retyped. Only a value with no spaces in
// it is read as a name that way, so a check written as a comparison ("partner equal \"ashe\"")
// contributes nothing and no operator list has to be kept in step here.
function variableCandidates(script: string, names: CompletionNames, skipLine: number): Candidate[] {
  const seen = new Map<string, string>();
  words(names.variables).forEach((word) => {
    seen.set(lower(word), word);
  });
  lines(script).forEach((line, index) => {
    if (index === skipLine) return;
    const said = line.match(/^\s*>\s*(?:remember|forget|if):\s*([^=]+?)\s*(?:=.*)?$/i);
    const name = said?.[1]?.trim();
    if (!name || /\s/.test(name) || ENGINE_ID.test(name) || seen.has(lower(name))) return;
    seen.set(lower(name), name);
  });
  return [...seen.values()].map((word): Candidate => ({
    label: word, insert: word, kind: 'variable', detail: 'something the story remembers', match: word,
  }));
}

function priceCandidates(ladder: readonly number[], tail: string): Candidate[] {
  const closed = tail.startsWith(']');
  const source: readonly unknown[] = Array.isArray(ladder) ? ladder : DEFAULT_LADDER;
  const rungs = source
    .map((value) => Number(value))
    .filter((value) => Number.isInteger(value) && value > 0);
  return [0, ...new Set(rungs)].map((price): Candidate => ({
    label: String(price),
    insert: closed ? String(price) : `${price}]`,
    caret: String(price).length + 1,
    kind: 'price',
    detail: price === 0 ? 'free' : 'a price this season uses',
    match: String(price),
  }));
}

function candidatesFor(
  context: Context,
  script: string,
  names: CompletionNames,
  ladder: readonly number[],
  tail: string,
  skipLine: number,
): Candidate[] {
  if (context.kind === 'scene') return sceneCandidates(script, skipLine);
  if (context.kind === 'character') return characterCandidates(names);
  if (context.kind === 'mood') return moodCandidates(names, tail);
  if (context.kind === 'location') return locationCandidates(names);
  if (context.kind === 'variable') return variableCandidates(script, names, skipLine);
  if (context.kind === 'price') return priceCandidates(ladder, tail);
  return DIRECTIVES.map((entry): Candidate => ({
    label: entry.word, insert: entry.insert, kind: 'directive', detail: entry.detail, match: entry.word,
  }));
}

/** A candidate with its rank: the tier it matched in, how often the script says it, its offered order. */
interface Scored {
  candidate: Candidate;
  tier: number;
  order: number;
  uses: number;
}

// Exact prefix matches first, then the ones that match without regard to case, and inside each of
// those the words the script already uses most, so the list leans towards the episode in front of
// the writer. Anything still tied keeps the order it was offered in — the ladder low to high, the
// directives as the grammar lists them, the book's own order for a table, and the script's own
// order for its scenes — so one caret can only ever produce one list.
function rank(candidates: Candidate[], prefix: string, haystack: string): Candidate[] {
  const wanted = lower(prefix);
  const scored: Scored[] = [];
  candidates.forEach((candidate, order) => {
    const match = String(candidate.match ?? candidate.label);
    let tier: number;
    if (match.startsWith(prefix)) tier = 0;
    else if (lower(match).startsWith(wanted)) tier = 1;
    else return;
    scored.push({
      candidate, tier, order, uses: frequency(haystack, match),
    });
  });
  scored.sort((left, right) => left.tier - right.tier
    || right.uses - left.uses
    || left.order - right.order);
  return scored.map((entry) => entry.candidate);
}

// The boundary is as forgiving as the frozen one: nonsense in gets an empty list out, never a
// throw (complete.test.mjs:290-298 hands it null, a negative caret, a caret past the end, and no
// caret at all). The signature is wider than CompletionFn for that reason and assignable to it.
export function complete(
  script: string | null | undefined,
  caretOffset: number | null | undefined,
  options: CompletionOptions = {},
): Completion {
  const nothing = (anchor: number, prefix = ''): Completion => ({ anchor, prefix, items: [] });
  if (typeof script !== 'string') return nothing(0);
  const caret = Math.max(0, Math.min(Math.trunc(Number(caretOffset)) || 0, script.length));
  const lineStart = script.lastIndexOf('\n', caret - 1) + 1;
  const lineBreak = script.indexOf('\n', caret);
  const head = script.slice(lineStart, caret);
  const tail = script.slice(caret, lineBreak < 0 ? script.length : lineBreak).replace(/\r$/, '');

  const context = contextAt(head, tail);
  if (!context) return nothing(caret);
  const { prefix } = context;
  const anchor = caret - prefix.length;
  if (!prefix && !OPEN_ON_EMPTY.has(context.key)) return nothing(anchor);

  const caretLine = script.slice(0, lineStart).split('\n').length - 1;
  const candidates = candidatesFor(context, script, options.names ?? {}, options.ladder ?? DEFAULT_LADDER, tail, caretLine)
    // Belt and braces: whatever a table holds, an id is not a word and is never put in front of
    // a writer.
    .filter((candidate) => candidate.insert && !ENGINE_ID.test(String(candidate.match ?? candidate.label)));
  const items = rank(candidates, prefix, lower(script)).slice(0, MAX_ITEMS).map((candidate): CompletionItem => ({
    label: candidate.label,
    insert: candidate.insert,
    kind: candidate.kind,
    detail: candidate.detail,
    caret: typeof candidate.caret === 'number' && Number.isInteger(candidate.caret) ? candidate.caret : candidate.insert.length,
  }));
  return { anchor, prefix, items };
}

export { DEFAULT_LADDER, MAX_ITEMS };
